import {
  Box,
  FieldLabel,
  FileUploader,
  Flex,
  Form,
  type OperationType,
  RadioGroupField,
  SelectField,
  Space,
  Text,
  TextAreaInputField,
  theme,
} from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { MAX_FILE_UPLOAD_LENGTH } from '@/features/comment/constants';
import { TagSelectFieldForEdit } from '@/features/tag/components';
import { tagType } from '@/features/tag/models';
import { useHierarchyLabels } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import { isRS } from '@/utils';
import { type FC, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  useCommentInputCandidateOrganizations,
  useCommentInputCandidateUsers,
} from '../../../hooks/input-candidate';
import {
  type CommentFormSchema,
  CommentPublishTypeLabelModifier,
  type commentKeyType,
  commentPublishType,
} from '../../../models/comment';
import type { PinpointModel } from '../../../models/pinpoint';
import { PinpointDisplay } from '../../pinpoint';
import { CommentStrictFields } from '../common/CommentStrictFields';

export const COMMENT_FORM_ID = 'comment-form';

const commentPublishTypeOptions = commentPublishType
  .extract(['private', 'strict'])
  .options.map((value) => ({
    value,
    label: CommentPublishTypeLabelModifier[value],
  }));

type FormValues = z.infer<typeof CommentFormSchema>;
type InitialFormValues = Partial<FormValues>;

export const CommentForm: FC<{
  type: OperationType;
  initialValues?: Partial<FormValues>;
  pinpoint: z.infer<typeof PinpointModel> | null | undefined;
  commentTarget: {
    key: z.infer<typeof commentKeyType>;
    objectId: string;
    objectAssigneeIds?: string[] | null;
  };
  onSubmit: (values: FormValues, file: File[]) => Promise<void>;
  onFileChanged: (changed: boolean) => void;
}> = ({
  type,
  initialValues,
  pinpoint,
  commentTarget,
  onSubmit,
  onFileChanged,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const {
    control,
    setValue,
    resetField,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, errors, isSubmitting, isSubmitSuccessful },
  } = useFormContext<InitialFormValues, unknown, FormValues>();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { fields: attachmentFields, remove: removeAttachment } = useFieldArray({
    control,
    name: 'attachments',
  });

  const publishType = watch('publishType');

  const inputCandidateCommonParams = {
    key: commentTarget.key,
    objectId: commentTarget.objectId,
  };

  const { data: me } = useMe();
  const { data: candidatesUserIds } = useCommentInputCandidateUsers({
    params: inputCandidateCommonParams,
    select: (d) => d.userIds,
  });

  const { data: candidatesOrganizationIds } =
    useCommentInputCandidateOrganizations({
      params: inputCandidateCommonParams,
      select: (d) => d.organizationIds,
    });

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });

  useEffect(() => {
    if (!isDirty) {
      reset((prev) => ({
        ...prev,
        ...initialValues,
      }));
    }
  }, [isDirty, reset, initialValues]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      id={COMMENT_FORM_ID}
      layout="vertical"
      onFinish={handleSubmit(async (values) => {
        await onSubmit(values, uploadedFiles);
      })}
    >
      <Flex vertical gap={16}>
        <Space direction="vertical">
          <FieldLabel text={t('comment.pinpoint')} />
          {pinpoint && <PinpointDisplay pinpoint={pinpoint} />}
        </Space>

        <Box>
          <Flex vertical gap={8}>
            <RadioGroupField
              name="publishType"
              label={t('scope-of-disclosure')}
              options={commentPublishTypeOptions}
              control={control}
              errors={errors}
              required
              marginBottom={0}
              shouldCoexistOnChange
              onChange={({ target: { value } }) => {
                const selectedPublishType = commentPublishType.parse(value);

                if (selectedPublishType === 'private') {
                  if (me) {
                    resetField('assigneeIds', { defaultValue: [me.id] });
                  }
                  resetField('relatedUserIds', { defaultValue: [] });
                  resetField('sharedOrganizationIds', { defaultValue: [] });
                }
                if (selectedPublishType === 'strict') {
                  const newAssigneeIds = commentTarget.objectAssigneeIds ?? [];

                  if (me && candidatesUserIds?.includes(me.id)) {
                    newAssigneeIds.push(me.id);
                  }

                  setValue('assigneeIds', Array.from(new Set(newAssigneeIds)));

                  // 新規作成または、プライベートから限定公開に変更する場合
                  setValue(
                    'sharedOrganizationIds',
                    type === 'new' || initialValues?.publishType === 'private'
                      ? candidatesOrganizationIds
                      : initialValues?.sharedOrganizationIds,
                  );
                }
              }}
            />

            {publishType === 'strict' ? (
              <Box background={token.colorBgLayout} padding="16px 16px 0 16px">
                <CommentStrictFields />
              </Box>
            ) : (
              <Box background={token.colorBgLayout} padding={16}>
                <Text
                  weight="regular"
                  text={t('comment.form-description.private-comments')}
                />
              </Box>
            )}
          </Flex>
        </Box>

        <TextAreaInputField
          name="content"
          label={t('comment.content')}
          control={control}
          errors={errors}
          rows={4}
          required
          style={{ marginBottom: 0 }}
        />

        <TagSelectFieldForEdit
          name="tagIds"
          control={control}
          errors={errors}
          type={tagType.Values.comment}
          startYear="current"
          endYear="current"
        />

        <SelectField
          name="labelId"
          label={t('comment.label')}
          control={control}
          options={hierarchyLabelOptions}
          errors={errors}
          allowClear
          useNotInOption
          wrapperStyle={{ marginBottom: 0 }}
        />

        <Form.Item label={<FieldLabel text={t('attachment-file')} />}>
          <FileUploader
            files={uploadedFiles}
            maxFiles={MAX_FILE_UPLOAD_LENGTH}
            maxFileSelection={MAX_FILE_UPLOAD_LENGTH}
            acceptableImage={!isRS()}
            onChange={(acceptedFiles) => {
              setUploadedFiles((prev) =>
                [...prev, ...acceptedFiles].slice(0, MAX_FILE_UPLOAD_LENGTH),
              );
              onFileChanged(true);
            }}
            handleCancel={(index) => {
              const newFiles = [
                ...uploadedFiles.slice(0, index),
                ...uploadedFiles.slice(index + 1),
              ];

              setUploadedFiles(newFiles);
              onFileChanged(!!newFiles.length);
            }}
            storedFiles={{
              items: attachmentFields.map((field) => ({
                name: field.fileName,
                url: field.url,
                fileContentType: field.fileContentType,
              })),
              onCancel: removeAttachment,
            }}
          />
        </Form.Item>
      </Flex>
    </Form>
  );
};
